import { createAsyncThunk } from '@reduxjs/toolkit'

import type { RootState } from '../../index'
import { apiGET, apiPOST } from '../../lib/api'
import {
  DefectAssistantDefectCodeAdvice,
  DefectAssistantImage,
  DefectAssistantSuggestionsResponse,
  DefectAssistantValidateRequest,
  DefectAssistantValidateResponse,
  DuplicateCommentResult,
  FinalizationSnapshot,
  QnA,
  ReportDefectBody,
  ReportDefectQuestion,
} from './types'

/**
 * Mirrors the backend's QnA.isUnanswered(): a question is unanswered exactly when its answers list
 * is null or empty, uniformly across all answer types. The multi-select completeness question carries
 * an explicit NOTHING_TO_ADD option, so "the description is already complete" is submitted as a
 * non-empty list rather than relying on an empty-vs-null distinction.
 */
export const isQuestionUnanswered = (qna: QnA): boolean =>
  qna.answers == null || qna.answers.length === 0

const AI_MAX_BASE64_BYTES = 5 * 1024 * 1024

// Past these the assistant counts as unavailable and the defect is reported without it. Analysis
// uploads images, so it gets longer.
const ANALYZE_TIMEOUT_MS = 45_000
const VALIDATE_TIMEOUT_MS = 30_000

const compressImageForAi = (image: DefectAssistantImage): Promise<DefectAssistantImage> => {
  if (image.base64.length <= AI_MAX_BASE64_BYTES) return Promise.resolve(image)

  return new Promise((resolve) => {
    const img = new window.Image()
    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = img.naturalWidth
      canvas.height = img.naturalHeight
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        resolve(image)
        return
      }
      ctx.drawImage(img, 0, 0)
      let quality = 0.85
      let compressed = canvas.toDataURL('image/jpeg', quality).split(',')[1]
      while (compressed.length > AI_MAX_BASE64_BYTES && quality > 0.1) {
        quality = Math.round((quality - 0.1) * 10) / 10
        compressed = canvas.toDataURL('image/jpeg', quality).split(',')[1]
      }
      resolve({ base64: compressed, mimeType: 'image/jpeg' })
    }
    img.onerror = () => resolve(image)
    img.src = `data:${image.mimeType};base64,${image.base64}`
  })
}

export const analyzeDefect = async (
  comment: string,
  images: DefectAssistantImage[],
  vehicleType: string,
  serialNumber: string,
  isRetry: boolean,
  discardedDefectCodes: string[]
): Promise<DefectAssistantSuggestionsResponse> => {
  const response = await apiPOST<DefectAssistantSuggestionsResponse>(
    '/defectAssistant/comment',
    {
      comment,
      images,
      vehicleType,
      serialNumber,
      isRetry,
      discardedDefectCodes,
    },
    { timeout: ANALYZE_TIMEOUT_MS }
  )
  return {
    suggestions: response.suggestions ?? [],
    metaOptions: response.metaOptions ?? [],
    generationId: response.generationId ?? null,
    futureQuestions: response.futureQuestions ?? [],
  }
}

export const fetchDefectCodeAdvice = async (
  generationId: string,
  prefix: string
): Promise<DefectAssistantDefectCodeAdvice> => {
  return await apiPOST<DefectAssistantDefectCodeAdvice>('/defectAssistant/defectCodeAdvice', {
    generationId,
    prefix,
  })
}

export const getNextDefectQuestion = async (
  body: ReportDefectBody,
  questionAnswers: QnA[],
  images: DefectAssistantImage[],
  streamingCapable: boolean
): Promise<ReportDefectQuestion> => {
  return await apiPOST<ReportDefectQuestion>('/defectAssistant/questions', {
    body,
    questionAnswers,
    images,
    streamingCapable,
  })
}

export const fetchSuggestedAmendComment = async (
  suggestedCommentId: string
): Promise<DuplicateCommentResult> => {
  return await apiGET<DuplicateCommentResult>(
    `/defectAssistant/duplicateComment/${encodeURIComponent(suggestedCommentId)}`
  )
}

export const fetchSummarySnapshot = async (
  finalizationId: string
): Promise<FinalizationSnapshot> => {
  return await apiGET<FinalizationSnapshot>(
    `/defectAssistant/finalization/${encodeURIComponent(finalizationId)}`
  )
}

export const analyzeSuggestionsAction = createAsyncThunk<
  DefectAssistantSuggestionsResponse,
  void,
  { state: RootState }
>('createDefectAssistant/analyzeSuggestions', async (_, thunkApi) => {
  const state = thunkApi.getState().createDefectAssistant
  const response = await analyzeDefect(
    state.body.userComment,
    await Promise.all(state.images.map(compressImageForAi)),
    state.body.vehicleSeries,
    state.body.installationGroup,
    state.suggestionAttempts > 1,
    state.discardedDefectCodes
  )
  const { generationId } = response
  if (generationId) {
    response.suggestions.forEach((s) => {
      void thunkApi.dispatch(fetchDefectCodeAdviceAction({ generationId, prefix: s.defectCode }))
    })
  }
  return response
})

export const fetchDefectCodeAdviceAction = createAsyncThunk(
  'createDefectAssistant/fetchDefectCodeAdvice',
  ({ generationId, prefix }: { generationId: string; prefix: string }) =>
    fetchDefectCodeAdvice(generationId, prefix)
)

export const fetchSuggestedAmendCommentAction = createAsyncThunk(
  'createDefectAssistant/fetchSuggestedAmendComment',
  ({ suggestedCommentId }: { suggestedCommentId: string }) =>
    fetchSuggestedAmendComment(suggestedCommentId)
)

export const pollSummaryAction = createAsyncThunk(
  'createDefectAssistant/pollSummary',
  ({ finalizationId }: { finalizationId: string }) => fetchSummarySnapshot(finalizationId)
)

/**
 * @param blockingSummary re-run the turn asking the backend to write the description inline instead
 *        of streaming it. The documented fallback for a stream that broke or aged out; the normal
 *        path leaves it unset and polls with the returned finalizationId.
 */
export const advanceQuestionFlowAction = createAsyncThunk<
  ReportDefectQuestion,
  { blockingSummary: boolean } | undefined,
  { state: RootState }
>('createDefectAssistant/advanceQuestionFlow', async (arg, thunkApi) => {
  const state = thunkApi.getState().createDefectAssistant
  // Images go on every turn: an image-only report has nothing else for the later steps to work from.
  const response = await getNextDefectQuestion(
    state.body,
    state.questionAnswers,
    await Promise.all(state.images.map(compressImageForAi)),
    !arg?.blockingSummary
  )
  return {
    ...response,
    questionAnswers:
      response.questionAnswers.length > 0 ? response.questionAnswers : state.questionAnswers,
  }
})

export const validateDefectInfo = async (
  payload: DefectAssistantValidateRequest
): Promise<DefectAssistantValidateResponse> => {
  return await apiPOST<DefectAssistantValidateResponse>('/defectAssistant/validate', payload, {
    timeout: VALIDATE_TIMEOUT_MS,
  })
}

/**
 * Fire-and-forget pre-warming of the AI defect-assistant caches for a vehicle, so the later
 * suggestion call is fast. Call it when the worker selects a vehicle (and on form-land with a
 * preselected vehicle). Best-effort: failures are swallowed and never block the form.
 */
export const cacheWarmup = (vehicleType: string, serialNumber: string): void => {
  if (!vehicleType || !serialNumber) return
  void apiPOST<{ status: string }>('/defectAssistant/cacheWarmup', {
    vehicleType,
    serialNumber,
  }).catch(() => {
    // Non-critical optimisation — ignore failures.
  })
}
